import { useEffect, useState } from "react";
import api from "@/lib/api";
import { GraduationCap, Phone, MessageSquare, ExternalLink, MessageCircleQuestion } from "lucide-react";
import TrainingAdEnquiryModal from "./TrainingAdEnquiryModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public-facing horizontal strip of active Training Advertisements.
 * Placed on Home, Discover, and category-scoped pages.
 * - category: optional TRAINING category key OR a talent category (mapped client-side by the API's
 *   filter param — pass the talent key directly for now; backend accepts training keys only)
 * - Falls back to unfiltered list of active ads.
 */
export default function TrainingAdsStrip({ category, city, limit = 6, className = "" }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enquireAd, setEnquireAd] = useState(null);

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (city) params.city = city;
    if (limit) params.limit = limit;
    api.get("/training-ads", { params })
      .then((r) => {
        const list = r.data.ads || [];
        setAds(list);
        // Fire impression pings (one per ad on this render)
        list.forEach((a) => api.post(`/training-ads/${a.id}/impression`).catch(() => {}));
      })
      .catch(() => setAds([]))
      .finally(() => setLoading(false));
  }, [category, city, limit]);

  if (loading || ads.length === 0) return null;

  return (
    <section data-testid="training-ads-strip" className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2 px-1">
        <GraduationCap size={14} className="text-[#F5C462]"/>
        <p className="text-[11px] uppercase tracking-[0.15em] text-[#F5C462] font-semibold">
          Learn from top academies
        </p>
        <span className="text-[10px] text-zinc-500 ml-1">Sponsored</span>
      </div>
      <div className="flex overflow-x-auto gap-3 pb-2 -mx-4 px-4 [&::-webkit-scrollbar]:hidden snap-x">
        {ads.map((ad) => (
          <TrainingAdCard key={ad.id} ad={ad} onEnquire={() => setEnquireAd(ad)}/>
        ))}
      </div>
      <TrainingAdEnquiryModal open={!!enquireAd} ad={enquireAd} onClose={() => setEnquireAd(null)}/>
    </section>
  );
}

function TrainingAdCard({ ad, onEnquire }) {
  const bannerUrl = ad.banner_path ? `${BACKEND}/api/training-ads/media/${ad.banner_path}` : "";
  const logoUrl = ad.logo_path ? `${BACKEND}/api/training-ads/media/${ad.logo_path}` : "";
  const openWebsite = () => {
    api.post(`/training-ads/${ad.id}/click`).catch(() => {});
    window.open(ad.website_url, "_blank", "noopener,noreferrer");
  };
  const waLink = ad.whatsapp ? `https://wa.me/${String(ad.whatsapp).replace(/\D/g, "")}` : null;

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={openWebsite}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openWebsite(); } }}
      data-testid={`training-ad-${ad.id}`}
      data-href={ad.website_url}
      className="shrink-0 snap-start w-72 rounded-2xl overflow-hidden border border-zinc-800 bg-gradient-to-b from-[#180D0D] to-[#0A0605] hover:border-[#F5C462]/40 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F5C462]/50"
    >
      {bannerUrl && (
        <div className="h-32 w-full bg-black overflow-hidden">
          <img src={bannerUrl} alt={ad.academy_name} loading="lazy" className="w-full h-full object-cover"/>
        </div>
      )}
      <div className="p-3">
        <div className="flex items-center gap-2">
          {logoUrl && (
            <img src={logoUrl} alt="" loading="lazy" className="w-9 h-9 rounded-lg object-cover border border-zinc-800 bg-black shrink-0"/>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate flex items-center gap-1">
              {ad.academy_name}
              <ExternalLink size={11} className="text-zinc-400"/>
            </p>
            <p className="text-[11px] text-zinc-400 truncate">{ad.city}{ad.state ? `, ${ad.state}` : ""}</p>
          </div>
        </div>
        <p className="text-xs text-zinc-300 mt-2 line-clamp-2 leading-snug">{ad.description}</p>
        {(ad.phone || waLink) && (
          <div className="mt-3 flex gap-2">
            {ad.phone && (
              <a
                data-testid={`training-ad-call-${ad.id}`}
                href={`tel:${ad.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 h-8 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-xs font-semibold inline-flex items-center justify-center gap-1"
              >
                <Phone size={11}/> Call
              </a>
            )}
            {waLink && (
              <a
                data-testid={`training-ad-wa-${ad.id}`}
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 h-8 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-black text-xs font-semibold inline-flex items-center justify-center gap-1"
              >
                <MessageSquare size={11}/> WhatsApp
              </a>
            )}
          </div>
        )}
        <button
          data-testid={`training-ad-enquire-${ad.id}`}
          onClick={(e) => { e.stopPropagation(); onEnquire?.(); }}
          className="mt-2 w-full h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-100 text-xs font-semibold inline-flex items-center justify-center gap-1"
        >
          <MessageCircleQuestion size={11}/> Enquire
        </button>
      </div>
    </div>
  );
}
